/**
 * Markdown Parser Utility
 * Extracts task lists and checkboxes from markdown content
 */

export interface MarkdownTask {
  id: string;
  text: string;
  completed: boolean;
  line: number;
  level: number;
  context: string[];
}

export interface MarkdownSection {
  title: string;
  level: number;
  tasks: MarkdownTask[];
  subsections: MarkdownSection[];
}

export interface ParsedMarkdown {
  title: string;
  sections: MarkdownSection[];
  allTasks: MarkdownTask[];
  totalTasks: number;
  completedTasks: number;
  progressPercentage: number;
}

/**
 * Parse markdown content and extract task lists with context
 */
export function parseMarkdownTasks(content: string): ParsedMarkdown {
  const lines = content.split("\n");
  const sections: MarkdownSection[] = [];
  const allTasks: MarkdownTask[] = [];
  let currentSection: MarkdownSection | null = null;
  const sectionStack: MarkdownSection[] = [];
  let documentTitle = "";
  let contextLines: string[] = [];

  lines.forEach((line, index) => {
    // Extract document title (first H1)
    if (!documentTitle && line.match(/^# /)) {
      documentTitle = line.replace(/^# /, "").trim();
      return;
    }

    // Detect section headers
    const headerMatch = line.match(/^(#{1,6}) (.+)$/);
    if (headerMatch) {
      const level = headerMatch[1].length;
      const title = headerMatch[2].trim();

      const newSection: MarkdownSection = {
        title,
        level,
        tasks: [],
        subsections: [],
      };

      // Pop sections from stack until we find the parent level
      while (
        sectionStack.length > 0 &&
        sectionStack[sectionStack.length - 1].level >= level
      ) {
        sectionStack.pop();
      }

      // Add to parent or root
      if (sectionStack.length === 0) {
        sections.push(newSection);
      } else {
        sectionStack[sectionStack.length - 1].subsections.push(newSection);
      }

      sectionStack.push(newSection);
      currentSection = newSection;
      contextLines = [title]; // Start new context with section title
      return;
    }

    // Detect task items (checkboxes)
    const taskMatch = line.match(/^(\s*)- \[([ xX])\] (.+)$/);
    if (taskMatch) {
      const indentation = taskMatch[1].length;
      const completed = taskMatch[2].toLowerCase() === "x";
      const text = taskMatch[3].trim();

      const task: MarkdownTask = {
        id: `task-${index}`,
        text,
        completed,
        line: index + 1,
        level: Math.floor(indentation / 2),
        context: [...contextLines],
      };

      allTasks.push(task);

      if (currentSection) {
        currentSection.tasks.push(task);
      }
    } else if (line.trim() && !line.startsWith("#")) {
      // Add non-empty lines to context (max 3 lines)
      contextLines.push(line.trim());
      if (contextLines.length > 3) {
        contextLines.shift();
      }
    }
  });

  const completedTasks = allTasks.filter((t) => t.completed).length;
  const totalTasks = allTasks.length;
  const progressPercentage =
    totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  return {
    title: documentTitle || "Untitled Document",
    sections,
    allTasks,
    totalTasks,
    completedTasks,
    progressPercentage,
  };
}

/**
 * Filter tasks by completion status
 */
export function filterTasksByStatus(
  tasks: MarkdownTask[],
  filter: "all" | "completed" | "pending",
): MarkdownTask[] {
  switch (filter) {
    case "completed":
      return tasks.filter((t) => t.completed);
    case "pending":
      return tasks.filter((t) => !t.completed);
    default:
      return tasks;
  }
}

/**
 * Search tasks by text
 */
export function searchTasks(
  tasks: MarkdownTask[],
  query: string,
): MarkdownTask[] {
  if (!query.trim()) return tasks;

  const lowerQuery = query.toLowerCase();
  return tasks.filter(
    (task) =>
      task.text.toLowerCase().includes(lowerQuery) ||
      task.context.some((ctx) => ctx.toLowerCase().includes(lowerQuery)),
  );
}

/**
 * Group tasks by section
 */
export function groupTasksBySection(
  sections: MarkdownSection[],
): Map<string, MarkdownTask[]> {
  const grouped = new Map<string, MarkdownTask[]>();

  function traverse(section: MarkdownSection, path: string[] = []) {
    const currentPath = [...path, section.title];
    const pathString = currentPath.join(" > ");

    if (section.tasks.length > 0) {
      grouped.set(pathString, section.tasks);
    }

    section.subsections.forEach((subsection) =>
      traverse(subsection, currentPath),
    );
  }

  sections.forEach((section) => traverse(section));
  return grouped;
}
